import {Dispatch, useCallback, useState} from 'react';

import {FORM_ERROR} from 'final-form';

import {OtlRuleDataLimitationName} from '~/shared/store/models/MoneycardOtlRuleManagement';

const useOtlLimitationsInputs = (initialValues: {
  [key in OtlRuleDataLimitationName]: number;
}) => {
  const [singleTransactionValue, setSingleTransactionValue] = useState<number>(initialValues.SingleTransaction);
  const [dailyValue, setDailyValue] = useState<number>(initialValues.Daily);
  const [monthlyValue, setMonthlyValue] = useState<number>(initialValues.Monthly);

  const inputValidators: Record<OtlRuleDataLimitationName, ({value}: {value: number}) => string | undefined> = {
    [OtlRuleDataLimitationName.SINGLE_TRANSACTION]: ({value}) => {
      if (!value) {
        return;
      }
      if ((!!dailyValue && value > dailyValue) || (!!monthlyValue && value > monthlyValue)) {
        return 'transaction_limit_cant_be_higher_than_the_daily_or_monthly_limit';
      }
    },
    [OtlRuleDataLimitationName.DAILY]: ({value}) => {
      if (!value) {
        return;
      }
      if ((!!singleTransactionValue && value < singleTransactionValue) || (!!monthlyValue && value > monthlyValue)) {
        return 'daily_limit_cant_be_higher_than_monthly_limit_or_lower_than_transaction_limit';
      }
    },
    [OtlRuleDataLimitationName.MONTHLY]: ({value}) => {
      if (!value) {
        return;
      }
      if ((!!singleTransactionValue && value < singleTransactionValue) || (!!dailyValue && value < dailyValue)) {
        return 'monthly_limit_cant_be_lower_than_daily_limit_or_transaction';
      }
    },
  };

  const formValidator = ({SingleTransaction, Daily, Monthly}: {
    [key in OtlRuleDataLimitationName]: {value: number};
  }) => {
    const err: Record<typeof FORM_ERROR, string | undefined> = {
      [FORM_ERROR]: undefined,
    };

    if (SingleTransaction.value && Daily.value && SingleTransaction.value > Daily.value) {
      err[FORM_ERROR] = 'the_daily_limit_must_be_bigger_or_equal_to_the_single_transaction_limit';
    }
    if (Daily.value && Monthly.value && Daily.value > Monthly.value) {
      err[FORM_ERROR] = 'the_monthly_limit_must_be_bigger_or_equal_to_the_daily_limit';
    }
    if (SingleTransaction.value && Monthly.value && SingleTransaction.value > Monthly.value) {
      err[FORM_ERROR] = 'the_monthly_limit_must_be_bigger_or_equal_to_the_single_transaction_limit';
    }

    return err;
  };

  const onChange = useCallback((name: OtlRuleDataLimitationName, val: string) => {
    const settersMap: Record<OtlRuleDataLimitationName, Dispatch<number>> = {
      [OtlRuleDataLimitationName.SINGLE_TRANSACTION]: setSingleTransactionValue,
      [OtlRuleDataLimitationName.DAILY]: setDailyValue,
      [OtlRuleDataLimitationName.MONTHLY]: setMonthlyValue,
    };

    const setter = settersMap[name];
    const intValue = Number(val);
    setter(intValue || 0);
  }, []);

  return {
    singleTransactionValue,
    dailyValue,
    monthlyValue,
    inputValidators,
    formValidator,
    onChange,
  };
};

export default useOtlLimitationsInputs;
